import memoize from 'lodash/memoize';

import { mapReport } from '@webapp/account/src/resources/reports/map';

import { NpsType, roundFloat } from '@webapp/common/lib/ui';
import { updateItemInArray } from '@webapp/common/lib/utils';
import { QuestionType } from '@webapp/common/resources/survey';

import { FilterType } from './filter';
import type { Item, Report, Row } from './reports';
import type { ReportsState } from './state';

const IGNORE_CALC_REPORTS = [QuestionType.TEXT_PERCEPTION, QuestionType.CONJOINT];

export const calcRankImportance = (item: Item): Item => {
    item.rows.forEach((row) => {
        let totalCount = 0;
        let totalImportant = 0;
        row.cells.forEach(({ count }, cellIndex) => {
            totalImportant += count * (item.cols.length - cellIndex);
            totalCount += count;
        });
        row.importance =
            totalImportant === 0 && totalCount === 0 ? 0 : Math.round((totalImportant / totalCount) * 100) / 100;
    });

    return item;
};

export const calcNps = (item: Item): Item => {
    let badCount = 0,
        badPercent = 0,
        normalCount = 0,
        normalPercent = 0,
        goodCount = 0,
        goodPercent = 0;

    item.rows.forEach(({ count, npsType, percent }) => {
        switch (npsType) {
            case NpsType.BAD:
                badCount += count;
                badPercent += Number(percent);
                break;
            case NpsType.NORMAL:
                normalCount += count;
                normalPercent += Number(percent);
                break;
            case NpsType.GOOD:
                goodCount += count;
                goodPercent += Number(percent);
                break;
        }
    });

    item.cols.forEach((col) => {
        switch (col.npsType) {
            case NpsType.BAD:
                col.count = badCount;
                col.percent = Math.round(badPercent * 100) / 100;
                break;
            case NpsType.NORMAL:
                col.count = normalCount;
                col.percent = Math.round(normalPercent * 100) / 100;
                break;
            case NpsType.GOOD:
                col.count = goodCount;
                col.percent = Math.round(goodPercent * 100) / 100;
                break;
        }
    });

    return item;
};

export const calcStar = (item: Item): Item => {
    let totalCount = 0;
    let totalStars = 0;

    item.rows.forEach((row) => {
        totalCount += row.count;
        totalStars += Number(row.value1) * row.count;
    });

    item.average = totalStars / totalCount || 0;
    item.totalCount = totalCount || 0;

    return item;
};

export const calcRatingMatrix = (item: Item, addInfo: Record<any, any>): Item => {
    const answersTotalAvg = addInfo.answersTotalAvg || {};
    const answersTotalCSAT = addInfo.answersTotalCSAT || {};

    item.rows.forEach((row) => {
        row.average = answersTotalAvg[row.id];
        row.CSAT = answersTotalCSAT[row.id];
    });

    return item;
};

export const calcRatingCsi = (item: Item): Item => {
    // TODO: correct calc @didolf
    item.rows.forEach((row) => {
        let totalCount = 0;
        let starCount = 0;
        row.cells.forEach((cell, index) => {
            totalCount += cell.count;
            starCount += cell.count * (index + 1);
        });

        row.average = starCount === 0 ? 0 : Math.round((starCount / totalCount) * 100) / 100;
    });

    return item;
};

export const calcMatrixReversePercent = ({ totalCount }: Report, item: Item): void => {
    item.rows.forEach((row) => {
        row.cells.forEach((cell) => {
            cell.percent = roundFloat((cell.count / totalCount) * 100);
        });
    });
};

export const updateReportItem = (
    reports: Array<Report>,
    { item, set }: any,
    reportId: number,
    itemCallback: (item: Item) => void,
): void => {
    const itemId = item ? item.id : set.itemId;

    updateItemInArray(reports, reportId, (r: Report) => {
        updateItemInArray(r.items, itemId, itemCallback);
    });
};

export const calcRowAverage = (report: Report, row: Row, item: Item, cellTotalCount: number): Row => {
    const { totalCount, type } = report;
    if (type === QuestionType.DISTRIBUTE_SCALE) {
        row.average = totalCount ? Math.round((row.count / totalCount) * 100) / 100 : 0;
    } else if (type === QuestionType.SEM_DIFF) {
        const all = row.cells.reduce((acc, { count }) => acc + count, 0);
        const colsTotal = row.cells.reduce(
            (acc, { count }, idx) => acc + parseInt(item.cols[idx].headValue) * count,
            0,
        );

        row.average = colsTotal / all;

        if (!isFinite(row.average)) {
            row.average = 0;
        }
    } else {
        row.average = Math.round((cellTotalCount / row.cells?.length) * 100) / 100;
    }

    return row;
};

export const calcCellTotalCount = (row: Row, item: Item): number =>
    item.cols.reduce((acc, _, colIndex) => acc + (row.cells[colIndex] ? row.cells[colIndex].count : row.count), 0);

const with100PercentForLine = new Set([
    QuestionType.FEW_OF_LIST,
    QuestionType.SELECT_FEW_IMAGE,
    QuestionType.TEST_FEW_OF_LIST,
]);

const calcRowsTotalCount = memoize((rows) =>
    rows.reduce((acc, { count, filterType }) => (filterType !== FilterType.EXCLUDE ? acc + count : acc), 0),
);

export const calcPercent = (report: Report, row: Row, type: QuestionType, item: Item, cellTotalCount: number): Row => {
    const answeredCount = report.totalCount || item.totalCount; // TODO review item.totalCount
    const allByLine = with100PercentForLine.has(type);
    const totalCount = calcRowsTotalCount(item.rows);

    if (allByLine) {
        const percent = answeredCount !== 0 ? 100 / (answeredCount / row.count) : 0;
        const toFloat = Math.round(percent * 100) / 100;

        row.percent = isNaN(toFloat) ? 0 : toFloat;
    } else {
        const percent = totalCount !== 0 ? 100 / (totalCount / row.count) : 0;
        const toFloat = Math.round(percent * 100) / 100;

        row.percent = isNaN(toFloat) ? 0 : toFloat;
    }

    item.cols.forEach((_, colIndex) => {
        if (row.cells[colIndex]) {
            const percent = 100 / (cellTotalCount / row.cells[colIndex].count);
            row.cells[colIndex].percent = Math.round(percent * 100) / 100;

            if (isNaN(row.cells[colIndex].percent)) {
                row.cells[colIndex].percent = 0;
            }
        }
    });

    return row;
};

export const calcReportList = (reports: Array<Report>): void => {
    reports.forEach((report) => {
        const { type } = report;

        if (IGNORE_CALC_REPORTS.includes(type)) {
            return;
        }

        report.items.forEach((item) => {
            item.rows.forEach((row) => {
                const cellTotalCount = calcCellTotalCount(row, item);

                calcPercent(report, row, type, item, cellTotalCount);
                calcRowAverage(report, row, item, cellTotalCount);
            });

            switch (type) {
                case QuestionType.NPS:
                    calcNps(item);
                    break;
                case QuestionType.RANK:
                case QuestionType.TEST_RANK:
                    calcRankImportance(item);
                    break;
                case QuestionType.STAR:
                    calcStar(item);
                    break;
                case QuestionType.MATRIX_RATING:
                    calcRatingMatrix(item, report.addInfo || {});
                    break;
                case QuestionType.CSI:
                    calcRatingCsi(item);
                    break;
                case QuestionType.MATRIX_SINGLE_ANSWER:
                case QuestionType.MATRIX_FEW_ANSWERS:
                    calcMatrixReversePercent(report, item);
                    break;
            }
        });
    });
};

export const calcReports = (state: ReportsState): void => {
    calcReportList(state.reports);
};

export const getFormattedReports = (questions) => {
    const reportsRaw: Array<Report> = questions.map(mapReport);
    const questionsMapped = reportsRaw.map(({ question }) => question);

    return reportsRaw.map((report) => ({
        ...report,
        question: ((quest) => {
            if (quest.type === QuestionType.TEXT_BLOCK) {
                return {
                    ...quest,
                    params: {
                        ...quest.params,
                        textBlock:
                            typeof quest.params.textBlock !== 'string'
                                ? quest.params.textBlock
                                : quest.params.textBlock.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                                      const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                                      if (!q) return '';
                                      return `{Q${q.order}}`;
                                  }),
                    },
                };
            }
            return {
                ...quest,
                name: quest.name.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                    const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                    if (!q) return '';
                    return `{Q${q.order}}`;
                }),
                cols: quest.cols.map((col) => {
                    return {
                        ...col,
                        name:
                            typeof col.name !== 'string'
                                ? col.name
                                : col.name.toString().replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                                      const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                                      if (!q) return '';
                                      return `{Q${q.order}}`;
                                  }),
                    };
                }),
                rows: quest.rows.map((row) => {
                    return {
                        ...row,
                        value:
                            typeof row.value !== 'string'
                                ? row.value
                                : row.value.toString().replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                                      const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                                      if (!q) return '';
                                      return `{Q${q.order}}`;
                                  }),
                    };
                }),
            };
        })(report.question),
    }));
};
